<template>
  <CModal :show.sync="show" centered>
    <template #header>
      <div>
        <div class="d-flex">
          <CIcon name="cipSignal" size="custom-size" width="36" height="36"
                 class="mr-3 icon-circle icon-circle--primary"/>
          <div class="align-self-center">
            <h5 class="modal-title" v-translate translate-context="event.modal.create_new_event.title">
              Activate event</h5>
            <div class="modal-subtitle" v-translate translate-context="event.modal.create_new_event.description">
              Review your settings and enable the event
            </div>
          </div>
        </div>
      </div>
      <CButtonClose @click="show = false"/>
    </template>
    <PComponentLoader :loading="loading">
      <template v-if="!allMandatoryIsPresent">
        <Suggestions :suggestions="suggestions"/>
      </template>
      <template v-else-if="configuration.is_in_payment">
        <div v-translate translate-context="event.modal.publish.info">
          Processing Payment, please wait...
        </div>
      </template>
      <template v-else-if="configuration.require_payment">
        <CategorySelector
            :categories="planOptions"
            :currency="paymentCurrency"
            :selected="planOptionId"
            @change="onSelectPlanOption"
        />
        <div class="font-weight-semi-bold mb-2">
          <translate translate-context="event.modal.create_new_event.plan_options_selector">
            Review your settings
          </translate>
        </div>
        <PAlert icon="cipInfo" color="info" class="mb-2">
          <template>
            <div v-translate translate-context="event.modal.publish.info">
              After activation you will no longer be able to change the date and time zone of the event
            </div>
          </template>
        </PAlert>
        <div class="event-recap mb-3">
          <div class="d-flex justify-content-between align-items-center">
            <div class="d-flex">
              <div class="label mr-2">
                <CIcon name="cipGlobe" size="custom-size" width="16" class="mr-1" style="vertical-align: sub"/>
                <translate translate-context="event.modal.publish.start_date">Date and time zone</translate>
              </div>
              <div class="value">{{ currentEventStartDate|formatDate('L') }} - {{ timeZone }}</div>
            </div>
            <div>
              <CButton color="secondary" size="sm" @click="onEdit" class="btn-circle btn-circle--sm" varient="outline">
                <CIcon name="cipPencilLine" />
              </CButton>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        All ok, ready to publish!
        <Suggestions :suggestions="suggestions"/>
      </template>
    </PComponentLoader>
    <template #footer>
      <div class="d-flex justify-content-between w-100">
        <div class="mr-1">
          <CButton color="secondary" @click="show = false" v-translate>Cancel</CButton>
        </div>
        <div class="ml-1">
          <template v-if="!allMandatoryIsPresent">
            <PButton
                color="primary" class="mr-3"
                @click="onEdit">
              <translate>Edit</translate>
            </PButton>
            <PButton
                v-if="hasPerm('advanced.force_publish')"
                color="danger" class="mr-3"
                @click="onPublish(true)">
              <translate>Force</translate>
            </PButton>
          </template>
          <template v-else-if="configuration.require_payment">
            <template v-if="isSubscription">
              <PButton
                  color="primary" class="ml-1"
                  :disabled="!planOptionId"
                  v-if="!configuration.is_in_payment"
                  @click="onPayment(false)">
                <translate translate-context="event.modal.publish.info">Subscribe monthly for</translate>
                {{ planPrice|currency(paymentCurrency) }}
              </PButton>
              <PButton
                  color="primary" class="ml-1"
                  :disabled="!planOptionId"
                  v-if="!configuration.is_in_payment"
                  @click="onPayment(true)">
                <translate translate-context="event.modal.publish.info">Subscribe yearly for</translate>
                {{ yearlyPrice|currency(paymentCurrency) }}
              </PButton>
            </template>
            <template v-else-if="planPrice > 0">
              <PButton
                  color="primary" class="ml-1"
                  :disabled="!planOptionId"
                  v-if="!configuration.is_in_payment"
                  @click="onPayment(false)">
                <translate translate-context="event.modal.publish.info">Activate for</translate>
                {{ planPrice|currency(paymentCurrency) }}
              </PButton>
            </template>
            <template v-else-if="planPrice <= 0">
              <PButton
                  color="primary" class="ml-1"
                  :disabled="!planOptionId"
                  @click="onPublish">
                <translate translate-context="event.modal.publish.info">Activate for Free</translate>
              </PButton>
            </template>

          </template>
          <template v-else>
            <PButton
                v-if="allMandatoryIsPresent"
                color="primary" v-translate translate-context="event.modal.publish.actions" class="ml-1"
                @click="onPublish">
              Publish
            </PButton>
          </template>
        </div>
      </div>
    </template>
  </CModal>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import PButton from "@/components/PButton.vue";
import Suggestions from "@/domain/events/components/Suggestions.vue";
import PAlert from "@/components/PAlert.vue";
import PComponentLoader from "@/domain/core/components/PComponentLoader.vue";
import CategorySelector from "@/domain/events/components/CategorySelector.vue";

export default {
  name: "PublishEventModal",
  emits: ['published', 'edit', 'goToPayment'],
  components: {
    CategorySelector,
    PComponentLoader,
    PAlert,
    Suggestions,
    PButton,
  },
  data() {
    return {
      show: false,
      loading: true,
      planOptionId: null,
      createInvoice: false
    }
  },
  computed: {
    ...mapGetters('eventWizard', [
      'allMandatoryIsPresent',
      'suggestions',
      'planOptions',
      'configuration',
      'paymentCurrency',
    ]),
    ...mapGetters('event', [
      'currentEventStartDate',
      'timeZone',
    ]),
    selectedOption() {
      if (!this.planOptionId) return null
      return this.planOptions
          .flatMap(category => category.base_plans)
          .find(plan => plan.id === this.planOptionId)
    },
    planPrice() {
      if (!this.selectedOption) return 0
      if (this.isSubscription) {
        return this.selectedOption.monthly_price || 0
      }
      return this.selectedOption.price
    },
    yearlyPrice() {
      if (!this.selectedOption) return 0
      if (this.isSubscription) {
        return this.selectedOption.yearly_price || 0
      }
      return 0
    },
    isSubscription() {
      if (!this.selectedOption) return false
      return this.selectedOption.is_subscription
    }
  },

  methods: {
    ...mapActions('eventWizard', [
      'publish',
      'fetchPlanOptions',
      'fetchPublishValidation',
      'fetchFormData',
      'fetchEventConfiguration',
      'fetchPaymentCurrency',
    ]),
    async open() {
      this.show = true
      this.loading = true
      await this.fetchPublishValidation()
      await this.fetchPaymentCurrency()
      if (this.allMandatoryIsPresent && this.configuration.require_payment)
        await this.fetchPlanOptions()

      this.loading = false
    },
    onSelectPlanOption(planId) {
      this.planOptionId = planId
    },
    onEdit() {
      this.$emit('edit')
      this.show = false
    },
    async onPublish(force = false) {
      await this.publish({basePlanId: this.planOptionId, force: force})
      this.$emit('published')
      this.show = false
    },
    async onPayment(yearly = false) {
      this.$emit('goToPayment', {
        planId: this.planOptionId,
        isSubscription: this.isSubscription,
        createInvoice: this.createInvoice,
        yearly: yearly
      })
      this.show = false
    },
  }
}
</script>
